const GUIDE_STEPS = Object.freeze([
  {
    label: '1 OF 4',
    title: 'TAP SHARE',
    copy: 'In Safari, tap the Share button at the bottom of the screen.',
    visual: 'share',
  },
  {
    label: '2 OF 4',
    title: 'ADD TO HOME SCREEN',
    copy: 'Scroll the Share sheet if needed, then tap Add to Home Screen.',
    visual: 'home',
  },
  {
    label: '3 OF 4',
    title: 'OPEN AS WEB APP',
    copy: 'Make sure Open as Web App is turned on so Bolt Forge launches like a real app.',
    visual: 'toggle',
  },
  {
    label: '4 OF 4',
    title: 'TAP ADD',
    copy: 'Tap Add. Then launch Bolt Forge from the new icon on your iPhone or iPad Home Screen.',
    visual: 'add',
  },
]);

function make(tag, className = '', text = '') {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

function installGlyph() {
  const wrap = make('span', 'bf-ios-install-glyph');
  wrap.setAttribute('aria-hidden', 'true');
  wrap.innerHTML = `
    <svg viewBox="0 0 64 64" role="presentation">
      <rect x="13" y="7" width="38" height="50" rx="7" class="bf-ios-glyph__phone"/>
      <path d="M32 10v25m0-25-9 9m9-9 9 9" class="bf-ios-glyph__arrow"/>
      <path d="M22 42h20" class="bf-ios-glyph__bar"/>
      <circle cx="32" cy="50" r="2.6" class="bf-ios-glyph__home"/>
    </svg>`;
  return wrap;
}

function stepVisual(kind) {
  const visual = make('div', `bf-ios-guide__visual bf-ios-guide__visual--${kind}`);

  if (kind === 'share') {
    visual.innerHTML = `
      <div class="bf-ios-phone-demo">
        <div class="bf-ios-browser-page"><b>BOLT FORGE</b><span>boltforge.grantfullen.com</span></div>
        <div class="bf-ios-browser-toolbar">
          <i></i><i></i>
          <span class="bf-ios-share-symbol" aria-hidden="true">
            <svg viewBox="0 0 48 48"><path d="M24 29V7m0 0-8 8m8-8 8 8M12 21H8v20h32V21h-4"/></svg>
          </span>
          <i></i><i></i>
        </div>
        <div class="bf-ios-guide__pointer">↓</div>
      </div>`;
    return visual;
  }

  if (kind === 'home') {
    visual.innerHTML = `
      <div class="bf-ios-sheet-demo">
        <div class="bf-ios-sheet-row"><span>Copy</span><em>□</em></div>
        <div class="bf-ios-sheet-row is-highlight"><span>Add to Home Screen</span><em>＋</em></div>
        <div class="bf-ios-sheet-row"><span>Add Bookmark</span><em>☆</em></div>
      </div>`;
    return visual;
  }

  if (kind === 'toggle') {
    visual.innerHTML = `
      <div class="bf-ios-a2hs-demo">
        <div class="bf-ios-app-preview"><span class="bf-ios-app-preview__logo">BF</span><b>Bolt Forge</b></div>
        <div class="bf-ios-toggle-row">
          <span>Open as Web App</span>
          <span class="bf-ios-toggle is-on"><i></i></span>
        </div>
      </div>`;
    return visual;
  }

  visual.innerHTML = `
    <div class="bf-ios-add-demo">
      <div class="bf-ios-add-demo__top"><span>Cancel</span><strong>Add to Home Screen</strong><b>ADD</b></div>
      <div class="bf-ios-app-preview"><span class="bf-ios-app-preview__logo">BF</span><b>Bolt Forge</b></div>
      <div class="bf-ios-home-preview">
        <span class="bf-ios-home-icon">BF</span>
        <small>Tap the new Home Screen icon</small>
      </div>
    </div>`;
  return visual;
}

export function shouldShowIosInstallHint(pwaManager) {
  const state = pwaManager?.state?.();
  return Boolean(state?.ios && !state?.installed);
}

export function openIosInstallGuide(stage, pwaManager, { startStep = 0 } = {}) {
  if (!stage || !shouldShowIosInstallHint(pwaManager)) return null;

  stage.querySelector('.bf-ios-install-guide')?.remove();
  let stepIndex = Math.max(0, Math.min(GUIDE_STEPS.length - 1, Number(startStep) || 0));

  const overlay = make('div', 'bf-ios-install-guide');
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Install Bolt Forge on iPhone or iPad');

  const card = make('section', 'bf-ios-guide__card');
  const close = make('button', 'bf-ios-guide__close', '×');
  close.type = 'button';
  close.setAttribute('aria-label', 'Close install tutorial');

  const eyebrow = make('span', 'bf-ios-guide__eyebrow');
  const title = make('h2', 'bf-ios-guide__title');
  const copy = make('p', 'bf-ios-guide__copy');
  const visualHost = make('div', 'bf-ios-guide__visual-host');

  const progress = make('div', 'bf-ios-guide__progress');
  const dots = GUIDE_STEPS.map(() => make('i'));
  dots.forEach((dot) => progress.append(dot));

  const actions = make('div', 'bf-ios-guide__actions');
  const back = make('button', 'bf-ios-guide__button bf-ios-guide__button--back', 'BACK');
  const next = make('button', 'bf-ios-guide__button bf-ios-guide__button--next', 'NEXT');
  back.type = 'button';
  next.type = 'button';
  actions.append(back, next);

  card.append(close, eyebrow, title, copy, visualHost, progress, actions);
  overlay.append(card);
  stage.append(overlay);

  function rememberSeen(value) {
    try {
      globalThis?.localStorage?.setItem?.('bolt-forge.ios-install-guide.v1', value);
    } catch {
      // The tutorial remains fully usable even if storage is unavailable.
    }
  }

  function renderStep() {
    const step = GUIDE_STEPS[stepIndex];
    eyebrow.textContent = `INSTALL BOLT FORGE • ${step.label}`;
    title.textContent = step.title;
    copy.textContent = step.copy;
    visualHost.replaceChildren(stepVisual(step.visual));
    dots.forEach((dot, index) => dot.classList.toggle('is-active', index === stepIndex));
    back.disabled = stepIndex === 0;
    next.textContent = stepIndex === GUIDE_STEPS.length - 1 ? 'GOT IT' : 'NEXT';
  }

  function dismiss(status = 'dismissed') {
    rememberSeen(status);
    overlay.remove();
    document.removeEventListener('keydown', onKey);
  }

  const onKey = (event) => {
    if (event.key === 'Escape') dismiss();
  };

  close.addEventListener('click', () => dismiss());
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) dismiss();
  });
  back.addEventListener('click', () => {
    if (stepIndex === 0) return;
    stepIndex -= 1;
    renderStep();
  });
  next.addEventListener('click', () => {
    if (stepIndex === GUIDE_STEPS.length - 1) {
      dismiss('completed');
      return;
    }
    stepIndex += 1;
    renderStep();
  });
  document.addEventListener('keydown', onKey);

  renderStep();
  next.focus();
  return overlay;
}

export function mountIosInstallHint(stage, pwaManager) {
  if (!stage || !shouldShowIosInstallHint(pwaManager)) return null;
  const existing = stage.querySelector('.bf-ios-install-hint');
  if (existing) return existing;

  const button = make('button', 'bf-ios-install-hint');
  button.type = 'button';
  button.setAttribute('aria-label', 'Install Bolt Forge on iPhone or iPad');
  button.append(installGlyph(), make('strong', '', 'INSTALL'));
  button.addEventListener('click', () => openIosInstallGuide(stage, pwaManager));
  stage.append(button);
  return button;
}
